/**
 * Hierarchy Validation Panel - Display validation issues with recommendations
 *
 * FERP Principles:
 * - Data-driven: All validation data from hierarchy
 * - No hardcoded business logic
 * - Complete type safety
 * - Clear issue descriptions and recommendations
 *
 * GitHub Issue: #340
 */

import React, { useMemo } from 'react';
import {
  Stack,
  Alert,
  Badge,
  Text,
  Group,
  Button,
  Box,
  ScrollArea,
  Collapse,
} from '@mantine/core';
import {
  IconCheck,
  IconAlertTriangle,
  IconX,
  IconInfoCircle,
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react';
import {
  type RoleHierarchy,
  type ValidationIssue,
  IssueSeverity,
  IssueType,
} from '../../data/repositories/HierarchyRepository';

export interface HierarchyValidationPanelProps {
  hierarchy: RoleHierarchy;
  tenantId: string;
  onFixIssue: () => void;
}

export function HierarchyValidationPanel({
  hierarchy,
  tenantId,
  onFixIssue,
}: HierarchyValidationPanelProps) {
  const [expandedIssues, setExpandedIssues] = React.useState<Set<number>>(
    new Set()
  );

  // Categorize issues by severity
  const { criticalIssues, warningIssues, infoIssues } = useMemo(() => {
    return {
      criticalIssues: hierarchy.validationIssues.filter(
        (i) => i.severity === IssueSeverity.Critical
      ),
      warningIssues: hierarchy.validationIssues.filter(
        (i) => i.severity === IssueSeverity.Warning
      ),
      infoIssues: hierarchy.validationIssues.filter(
        (i) => i.severity === IssueSeverity.Info
      ),
    };
  }, [hierarchy.validationIssues]);

  // Get severity color
  const getSeverityColor = (severity: IssueSeverity): string => {
    switch (severity) {
      case IssueSeverity.Critical:
        return 'red';
      case IssueSeverity.Warning:
        return 'orange';
      case IssueSeverity.Info:
        return 'blue';
      default:
        return 'gray';
    }
  };

  // Get issue type icon
  const getIssueTypeIcon = (issueType: IssueType) => {
    switch (issueType) {
      case IssueType.CircularDependency:
        return <IconAlertTriangle size={16} />;
      case IssueType.OrphanedRole:
        return <IconX size={16} />;
      case IssueType.DeepNesting:
        return <IconInfoCircle size={16} />;
      case IssueType.RedundantInheritance:
        return <IconInfoCircle size={16} />;
      case IssueType.PermissionConflict:
        return <IconAlertTriangle size={16} />;
      default:
        return <IconInfoCircle size={16} />;
    }
  };

  // Toggle issue expansion
  const toggleIssue = (index: number) => {
    setExpandedIssues((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // If no issues, show success message
  if (hierarchy.validationIssues.length === 0) {
    return (
      <Alert icon={<IconCheck size={16} />} color="green" variant="light">
        <Stack gap="xs">
          <Text fw={500} size="lg">
            No Validation Issues Found!
          </Text>
          <Text size="sm">
            Your role hierarchy is valid and well-structured. There are no
            circular dependencies, orphaned roles, or other issues detected.
          </Text>
          <Group gap="md" mt="xs">
            <Badge size="lg" color="green" variant="light">
              ✓ No Circular Dependencies
            </Badge>
            <Badge size="lg" color="green" variant="light">
              ✓ No Orphaned Roles
            </Badge>
            <Badge size="lg" color="green" variant="light">
              ✓ Healthy Nesting Depth
            </Badge>
          </Group>
        </Stack>
      </Alert>
    );
  }

  // Render issue card
  const renderIssue = (issue: ValidationIssue, index: number) => {
    const isExpanded = expandedIssues.has(index);

    return (
      <Alert
        key={index}
        icon={getIssueTypeIcon(issue.issueType)}
        color={getSeverityColor(issue.severity)}
        variant="light"
      >
        <Stack gap="xs">
          {/* Header */}
          <Group
            justify="space-between"
            style={{ cursor: 'pointer' }}
            onClick={() => toggleIssue(index)}
          >
            <Group gap="xs">
              <Badge size="sm" color={getSeverityColor(issue.severity)}>
                {issue.severity}
              </Badge>
              <Badge size="sm" variant="light">
                {issue.issueType.replace(/_/g, ' ')}
              </Badge>
              <Text fw={500}>{issue.roleName}</Text>
            </Group>
            {isExpanded ? (
              <IconChevronDown size={16} />
            ) : (
              <IconChevronRight size={16} />
            )}
          </Group>

          {/* Description */}
          <Text size="sm">{issue.description}</Text>

          {/* Expanded details */}
          <Collapse in={isExpanded}>
            <Stack gap="md" mt="md">
              {/* Recommendation */}
              <Box>
                <Text size="sm" fw={500} mb="xs">
                  Recommendation:
                </Text>
                <Text size="sm" c="dimmed">
                  {issue.recommendation}
                </Text>
              </Box>

              {/* Related roles */}
              {issue.relatedRoleIds.length > 0 && (
                <Box>
                  <Text size="sm" fw={500} mb="xs">
                    Related Roles:
                  </Text>
                  <Group gap="xs">
                    {issue.relatedRoleIds.map((roleId) => (
                      <Badge key={roleId} size="sm" variant="outline">
                        {roleId.slice(0, 12)}...
                      </Badge>
                    ))}
                  </Group>
                </Box>
              )}

              {/* Auto-fix button */}
              {issue.autoFixAvailable && (
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="filled"
                    color={getSeverityColor(issue.severity)}
                    leftSection={<IconCheck size={14} />}
                    onClick={() => {
                      // In production, this would call an auto-fix mutation
                      console.log('Auto-fixing issue:', issue);
                      onFixIssue();
                    }}
                  >
                    Auto-Fix Available
                  </Button>
                  <Text size="xs" c="dimmed">
                    Click to automatically resolve this issue
                  </Text>
                </Group>
              )}

              {!issue.autoFixAvailable && (
                <Text size="xs" c="dimmed">
                  Manual fix required - follow the recommendation above
                </Text>
              )}
            </Stack>
          </Collapse>
        </Stack>
      </Alert>
    );
  };

  return (
    <Stack gap="md">
      {/* Summary */}
      <Group gap="md">
        <Text size="lg" fw={500}>
          Validation Summary
        </Text>
        {criticalIssues.length > 0 && (
          <Badge size="lg" color="red">
            {criticalIssues.length} Critical
          </Badge>
        )}
        {warningIssues.length > 0 && (
          <Badge size="lg" color="orange">
            {warningIssues.length} Warnings
          </Badge>
        )}
        {infoIssues.length > 0 && (
          <Badge size="lg" color="blue">
            {infoIssues.length} Info
          </Badge>
        )}
      </Group>

      {/* Priority message */}
      {criticalIssues.length > 0 && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="filled">
          <Text fw={500}>Critical Issues Detected!</Text>
          <Text size="sm" mt="xs">
            {criticalIssues.length} critical issue
            {criticalIssues.length !== 1 ? 's' : ''} found that should be fixed
            immediately to ensure proper role hierarchy functionality.
          </Text>
        </Alert>
      )}

      {/* Issues list */}
      <ScrollArea h={500}>
        <Stack gap="md">
          {/* Critical issues first */}
          {criticalIssues.length > 0 && (
            <Box>
              <Text size="md" fw={500} c="red" mb="xs">
                Critical Issues
              </Text>
              <Stack gap="sm">
                {criticalIssues.map((issue, i) =>
                  renderIssue(
                    issue,
                    hierarchy.validationIssues.indexOf(issue)
                  )
                )}
              </Stack>
            </Box>
          )}

          {/* Warning issues */}
          {warningIssues.length > 0 && (
            <Box>
              <Text size="md" fw={500} c="orange" mb="xs">
                Warnings
              </Text>
              <Stack gap="sm">
                {warningIssues.map((issue, i) =>
                  renderIssue(
                    issue,
                    hierarchy.validationIssues.indexOf(issue)
                  )
                )}
              </Stack>
            </Box>
          )}

          {/* Info issues */}
          {infoIssues.length > 0 && (
            <Box>
              <Text size="md" fw={500} c="blue" mb="xs">
                Informational
              </Text>
              <Stack gap="sm">
                {infoIssues.map((issue, i) =>
                  renderIssue(
                    issue,
                    hierarchy.validationIssues.indexOf(issue)
                  )
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      </ScrollArea>

      {/* Help text */}
      <Text size="xs" c="dimmed">
        Click on an issue to expand and see detailed recommendations. Auto-fix is
        available for some issues.
      </Text>
    </Stack>
  );
}
